import { isRight } from 'fp-ts/Either';
import { use, useEffect, useState } from 'react';
import { TRPCContext } from '../contexts/TRPC';
import { captureAndReportError } from '../utils/errorReporting';
import { hasAdminAccess, isAuthenticated } from '../utils/roleUtils';
import { useAuth } from './useAuth';

export interface CategoryAssignmentAccess {
  isLoading: boolean;
  isAdmin: boolean;
  canAssignCategories: boolean;
  canViewAuditTrail: boolean;
  canPublish: boolean;
}

interface ModeratorCapabilities {
  canAssignCategories: boolean;
  canViewAuditTrail: boolean;
  canPublish: boolean;
}

const NO_MODERATOR_CAPABILITIES: ModeratorCapabilities = {
  canAssignCategories: false,
  canViewAuditTrail: false,
  canPublish: false,
};

/**
 * Resolves the current user's dapp category-assignment capabilities. Admins
 * qualify immediately; moderators fetch their capabilities from the global
 * permission system via `getCategoryAssignmentAccess` (decoupled from tickets).
 * The API enforces the same rules — this only drives UI gating.
 */
export function useCategoryAssignmentAccess(): CategoryAssignmentAccess {
  const [authState] = useAuth();
  const trpc = use(TRPCContext);

  const authed = isAuthenticated(authState);
  const role = authed ? authState.user.role : null;
  const isAdmin =
    authed && hasAdminAccess(authState.user.role, authState.user.email);

  const [moderatorCapabilities, setModeratorCapabilities] =
    useState<ModeratorCapabilities | null>(null);

  useEffect(() => {
    if (role !== 'moderator' || !trpc?.client) {
      setModeratorCapabilities(null);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const response =
          await trpc.client.getCategoryAssignmentAccess.query(undefined);
        if (cancelled) {
          return;
        }
        setModeratorCapabilities(
          isRight(response)
            ? {
                canAssignCategories: response.right.canAssignCategories,
                canViewAuditTrail: response.right.canViewAuditTrail,
                canPublish: response.right.canPublish,
              }
            : NO_MODERATOR_CAPABILITIES,
        );
      } catch (error) {
        captureAndReportError(
          'Failed to resolve category assignment access',
          error,
        );
        if (!cancelled) {
          setModeratorCapabilities(NO_MODERATOR_CAPABILITIES);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [role, trpc]);

  if (isAdmin) {
    return {
      isLoading: false,
      isAdmin: true,
      canAssignCategories: true,
      canViewAuditTrail: true,
      canPublish: true,
    };
  }

  if (role === 'moderator') {
    return {
      isLoading: moderatorCapabilities === null,
      isAdmin: false,
      canAssignCategories: moderatorCapabilities?.canAssignCategories === true,
      canViewAuditTrail: moderatorCapabilities?.canViewAuditTrail === true,
      canPublish: moderatorCapabilities?.canPublish === true,
    };
  }

  return {
    isLoading: false,
    isAdmin: false,
    canAssignCategories: false,
    canViewAuditTrail: false,
    canPublish: false,
  };
}
